import { isDefined, uuid } from '@interacta-shared/util';
import { toIUser } from '@interacta-shared/util-caronte';
import { parseIfNotNull } from '@interacta-shared/util-common';
import { toBasePostLite } from '@interacta-shared/util-post-lite';
import { toAttachmentDetails, toCoverImage } from '@interacta/data-access-post';
import { Server2Delta } from '@modules/core/helpers/delta/server-2-delta.class';
import {
    AISharedAttachmentReference,
    AISharedCommentReference,
    AISharedDeltaReference,
    AISharedGenericReference,
    AISharedMessageAttachment,
    AISharedMessageType,
    AISharedModelMessage,
    AISharedPostReference,
    AISharedReferenceAvailability,
    AISharedReferenceType,
    AISharedRole,
} from './ai-shared.model';

export function toAISharedModelMessage(value: any): AISharedModelMessage {
    return {
        id: value.id ? value.id : uuid(),
        role: AISharedRole.MODEL,
        messageType: AISharedMessageType.STANDARD,
        timestamp: value.creationTimestamp,
        alreadyAnimated: false,
        content: value.message,
        messageDelta: value.messageDelta
            ? new Server2Delta().process(value.messageDelta)
            : null,
        // Sources listed under the answer: `deltaSources` is the whole set the
        // in-text references point at, comments included.
        references: toAISharedGenericReferences(value.deltaSources ?? []),
        deltaReferences: toAISharedDeltaReferences(
            value.deltaReferences ?? [],
            value.deltaSources ?? [],
        ),
    };
}

export function toAISharedMessageAttachment(
    record: any,
): AISharedMessageAttachment {
    return {
        ...toReferenceAvailability(record),
        attachmentData: toAttachmentDetails(record),
    };
}

/**
 * `deltaSources` entries carry the same `{ type, post, attachment }` shape as
 * the footer `references`, so they go through the same deserializer; each
 * `deltaReferences` entry then points at them by id.
 */
export function toAISharedDeltaReferences(
    deltaReferences: Array<any>,
    deltaSources: Array<any>,
): AISharedDeltaReference[] {
    const sourceById = new Map<number, AISharedGenericReference>();
    for (const source of deltaSources) {
        const reference = toAISharedGenericReference(source);
        if (reference) sourceById.set(source.id, reference);
    }

    return deltaReferences.map((reference) => ({
        id: reference.id,
        sources: ((reference.sourceIds ?? []) as number[])
            .map((sourceId) => sourceById.get(sourceId))
            .filter(isDefined),
    }));
}

export function toAISharedGenericReferences(
    references: Array<any>,
): AISharedGenericReference[] {
    return references
        .map((r) => toAISharedGenericReference(r))
        .filter(isDefined);
}

/**
 * `canView` is a capability: absent means denied, so a payload predating the flag
 * renders every source as unavailable instead of opening one the user may not read.
 */
export function toReferenceAvailability(
    reference: any,
): AISharedReferenceAvailability {
    return {
        canView: reference.canView ?? false,
        deleted: reference.deleted ?? false,
        archived: reference.archived ?? false,
    };
}

export function toAISharedGenericReference(
    reference: any,
): AISharedGenericReference | undefined {
    if (reference.type === AISharedReferenceType.POST && reference.post) {
        return {
            ...toReferenceAvailability(reference),
            id: reference.id,
            post: {
                ...toBasePostLite(reference.post),
                mainAttachment:
                    parseIfNotNull(
                        reference.post.mainAttachment,
                        toAttachmentDetails,
                    ) ?? undefined,
                coverImage: toCoverImage(reference.post),
                creationTimestamp: reference.post.creationTimestamp
                    ? new Date(reference.post.creationTimestamp as number)
                    : undefined,
                description: reference.post.descriptionPlainText ?? null,
                descriptionDelta: reference.post.descriptionDelta
                    ? new Server2Delta().process(
                          reference.post.descriptionDelta as string,
                      )
                    : null,
            },
            type: AISharedReferenceType.POST,
        } satisfies AISharedPostReference;
    }

    if (
        reference.type === AISharedReferenceType.ATTACHMENT &&
        reference.attachment?.post &&
        reference.attachment?.attachmentData
    ) {
        return {
            ...toReferenceAvailability(reference),
            id: reference.id,
            attachment: {
                post: toBasePostLite(reference.attachment.post),
                attachmentData: toAttachmentDetails(
                    reference.attachment.attachmentData,
                ),
            },
            type: AISharedReferenceType.ATTACHMENT,
        } satisfies AISharedAttachmentReference;
    }

    if (reference.type === AISharedReferenceType.COMMENT && reference.comment) {
        return {
            ...toReferenceAvailability(reference),
            id: reference.id,
            comment: {
                id: reference.comment.id,
                creatorUser: reference.comment.creatorUser
                    ? toIUser(reference.comment.creatorUser)
                    : undefined,
                commentPlainText: reference.comment.commentPlainText ?? null,
                post: toBasePostLite(reference.comment.post),
            },
            type: AISharedReferenceType.COMMENT,
        } satisfies AISharedCommentReference;
    }

    return undefined;
}
